import React, { Component } from 'react'
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import numeral from 'numeral'
import 'numeral/locales/en-gb'
import { isEqual, result, find } from 'libraries/lodash'
import { pushUrl } from 'libraries/front-end-core/core/history'
import url from 'libraries/front-end-core/core/url'
import { MW_LIST_DEALS } from 'constants/controllers'
import Icon from 'components/shared/Icon'
import PanelTitle from 'pages/mwanalytics/PanelTitle'
import {
  FROM_OPERATOR,
  TO_OPERATOR
} from '../mwlistdeals/filters/DateRangeFilter'

numeral.locale('en-gb')

const MAX_TICKS = 15

class ChartBase extends Component {
  get config () {
    return result(
      this.props.documentConfiguration,
      [this.props.dealType, 'parameters', this.props.groupBy],
      null
    )
  }

  getItemLabel = config => ({ label, color }) => {
    if (this.props.labelFormat) {
      return {
        title: this.props.labelFormat(label),
        color
      }
    }
    const restrictingValues = result(config, 'restrictingValues', [])
    const whereMatchesLabel = find(restrictingValues, ({ value }) => value === label)
    return {
      title: result(whereMatchesLabel, 'displayName', label) || <em>Unlabeled</em>,
      color
    }
  }

  format = value => numeral(value).format(this.props.tickFormat || '0')

  get info () {
    return this.props.info
      ? (
        <div key='info' className='f7 tl flex justify-start items-start ml2'>
          <Icon className='mr2 pt2' name='icon-information-outline' />
          {this.props.info}
        </div>
        )
      : null
  }
}

class GraphChart extends ChartBase {
  state = {
    hilite: null,
    activeIndex: null
  }

  handleMouseMove = ({ activeIndex, isTooltipActive }) => {
    if (isTooltipActive && activeIndex != null) {
      this.setState({ activeIndex: Number(activeIndex) })
    }
  }

  handleMouseLeave = () => {
    this.setState({ activeIndex: null })
  }

  shouldComponentUpdate (props, state) {
    return !isEqual(this.state, state) || !isEqual(this.props, props)
  }

  get tickSpacing () {
    return Math.floor(this.props.stats.data[0].plot.length / MAX_TICKS) + 1
  }

  getChartData () {
    const { stats: { data } } = this.props
    return data[0].plot.map((point, idx) => {
      const entry = { x: point.x, range: point.range }
      data.forEach(({ name, plot }) => {
        entry[name] = plot[idx] ? plot[idx].y : 0
      })
      return entry
    })
  }

  handleClick = () => {
    const { activeIndex } = this.state
    if (activeIndex == null) return
    const { dealType, aol } = this.props
    const n = this.dateParameterName
    if (!n) return
    const point = this.props.stats.data[0].plot[activeIndex]
    if (!point || !point.range) return
    const [from, to] = point.range
    const parameters = [
      { n, o: FROM_OPERATOR, v: [from] },
      { n, o: TO_OPERATOR, v: [to] }
    ]
    const route = url(MW_LIST_DEALS, { aol, dealType }, { parameters: JSON.stringify(parameters) })
    pushUrl(route)
  }

  get dateParameterName () {
    const params = result(this.props.documentConfiguration, [this.props.dealType, 'parameters'], [])
    const typeNames = find(params, ({ filterGroup: { type: group }, type }) =>
      group === 'Basic' && type === 'DateParameterType'
    )
    return result(typeNames, 'name', null)
  }

  renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const getItemLabel = this.getItemLabel(this.config)
    const { stats: { data } } = this.props

    return (
      <div className='bg-white black pa4 br2 shadow-black-3 max-w14 nowrap'>
        <div className='b mb2'>{label}</div>
        <div className='flex flex-column' data-testid='graph-details'>
          {payload
            .filter(({ value }) => !!value)
            .map(({ name, value }, index) => {
              const series = data.find(d => d.name === name)
              const color = series ? series.color : '#000'
              return (
                <div key={index} className='items-center flex mb4'>
                  <div className='h0 pb3 w5 dib' style={{ background: color }}>&nbsp;</div>
                  <div className='w12 truncate pl4'>
                    {getItemLabel({ label: name, color }).title}
                  </div>
                  <div className='tr grow-1'>{this.format(value)}</div>
                </div>
              )
            })}
        </div>
      </div>
    )
  }

  render () {
    const { stats: { data }, displayName, groupBy } = this.props
    const { hilite } = this.state
    const getItemLabel = this.getItemLabel(this.config)
    const tickSpacing = this.tickSpacing
    const chartData = this.getChartData()
    // removed pointer style on this component as I couldn't find a way to make it apply consistently
    // and it was causing messy looking ux
    return (
      <div>
        <PanelTitle {...{ displayName }} />
        {data.length
          ? (
            <div>
              <ResponsiveContainer width='100%' height={350}>
                <ComposedChart
                  data={chartData}
                  onClick={this.handleClick}
                  onMouseMove={this.handleMouseMove}
                  onMouseLeave={this.handleMouseLeave}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey='x'
                    tickLine={false}
                    tickFormatter={(x, index) => index % tickSpacing === 0 ? x : ''}
                    angle={-45}
                    textAnchor='end'
                    height={60}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={this.format}
                    tickCount={2}
                  />
                  <Tooltip content={this.renderTooltip} />
                  {data.map(({ name, color }, index) => (
                    groupBy
                      ? (
                        <Bar
                          key={name}
                          dataKey={name}
                          fill={color}
                          stackId='stack'
                          opacity={hilite === null ? 1 : hilite === index ? 1 : 0.2}
                        />
                        )
                      : (
                        <Line
                          key={name}
                          dataKey={name}
                          stroke={color}
                          dot={false}
                          type='linear'
                          opacity={hilite === null ? 1 : hilite === index ? 1 : 0.2}
                        />
                        )
                  ))}
                  <Legend
                    content={({ payload }) => (
                      <div className='flex flex-wrap pt2'>
                        {payload.map(({ value, color }, index) => {
                          const item = data.find(d => d.name === value)
                          const seriesIndex = data.findIndex(d => d.name === value)
                          return (
                            <div
                              key={index}
                              className='w-50 flex items-center mb1 pr2'
                              onMouseEnter={() => this.setState({ hilite: seriesIndex })}
                              onMouseLeave={() => this.setState({ hilite: null })}
                            >
                              <div className='mr2 flex-shrink-0' style={{ width: 10, height: 10, background: color }} />
                              <span className='f7'>{item ? getItemLabel(item).title : value}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              {this.info}
            </div>
            )
          : (
            <div>No data available</div>
            )}
      </div>
    )
  }
}

class InfoBox extends ChartBase {
  render () {
    const config = this.config
    const getLabel = this.getItemLabel(config)
    const { displayName, stats } = this.props

    return (
      <div>
        <PanelTitle displayName={displayName} />
        <div className='flex flex-column grow-1'>
          {stats.data.map(({ label, name, plot }, key) => (
            <div className='flex pa4 flex-column' key={key}>
              <span>{getLabel({ label }).title}</span>
              <strong>{this.format(plot[0].y)}</strong>
            </div>
          ))}
        </div>
      </div>
    )
  }
}

class PieChartComponent extends ChartBase {
  get pieData () {
    return this.props.stats.data.map(
      ({ plot: [{ y: value }], label, color }) => ({
        name: label,
        value,
        color
      })
    )
  }

  render () {
    const { displayName, stats } = this.props
    const getItemLabel = this.getItemLabel(this.config)
    const pieData = this.pieData

    return (
      <div>
        <PanelTitle {...{ displayName }} />
        {pieData.length
          ? (
            <div>
              <PieChart width={300} height={380}>
                <Pie data={pieData} dataKey='value' nameKey='name' cx='50%' cy='40%' outerRadius={120}>
                  {pieData.map(({ color }, index) => (
                    <Cell key={index} fill={color} stroke='#FFF' strokeWidth={1} />
                  ))}
                </Pie>
                <Legend
                  content={({ payload }) => (
                    <div className='flex flex-wrap pt2'>
                      {payload.map(({ value, color }, index) => {
                        const { title } = getItemLabel({ label: value, color })
                        const dataItem = stats.data.find(d => d.label === value)
                        return (
                          <div key={index} className='w-50 flex items-center mb1 pr2'>
                            <div className='mr2 flex-shrink-0' style={{ width: 10, height: 10, background: color }} />
                            <span className='f7'>{title}{dataItem ? `: ${this.format(dataItem.plot[0].y)}` : ''}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                />
              </PieChart>
              {this.info}
            </div>
            )
          : (
            <div>No data available</div>
            )}
      </div>
    )
  }
}

export default props =>
  props.interval !== 'a'
    ? (
      <GraphChart {...props} />
      )
    : props.groupBy
      ? (
        <PieChartComponent {...props} />
        )
      : (
        <InfoBox {...props} />
        )
